import { decode } from '@msgpack/msgpack'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client'

import { SocketConfig } from '../../types'
import { logger } from '../../utils/logger'

type QueryValue = string | number | boolean | undefined | null

type GlobalSocketEvents = SocketTypeToPayload extends Record<string, unknown>
    ? SocketTypeToPayload
    : Record<string, unknown>

export interface SharedSocketMessage<
    TEvents extends Record<string, unknown>,
    TEvent extends keyof TEvents = keyof TEvents
> {
    type: TEvent
    payload: TEvents[TEvent]
    device?: string
    target?: string
}

export interface SharedSocketServiceOptions {
    config: SocketConfig
    queryParams?: Record<string, QueryValue>
    buildQueryParams?: () => Record<string, QueryValue>
    socketFactory?: (url: string, options?: Partial<ManagerOptions & SocketOptions>) => Socket
    allowedDevices?: string[]
}

const DEFAULT_ALLOWED_DEVICES: string[] = ['all', 'web']

const DEFAULT_RECONNECTION_DELAY = 2500

export class SharedSocketService<TEvents extends Record<string, unknown> = GlobalSocketEvents> {
    protected socket?: Socket
    protected subjects = new Map<keyof TEvents, Subject<unknown>>()

    public readonly isConnected$ = new BehaviorSubject<boolean>(false)
    public isConnected = false

    private config: SocketConfig
    private queryParams: Record<string, QueryValue>
    private readonly buildQueryParams?: () => Record<string, QueryValue>
    private readonly socketFactory: SharedSocketServiceOptions['socketFactory']
    private readonly allowedDevices: string[]

    constructor(options: SharedSocketServiceOptions) {
        this.config = options.config
        this.queryParams = options.queryParams ?? {}
        this.buildQueryParams = options.buildQueryParams
        this.socketFactory = options.socketFactory
        this.allowedDevices = options.allowedDevices ?? DEFAULT_ALLOWED_DEVICES
    }

    public updateConfig(config: SocketConfig): void {
        this.config = config
        if (this.socket) {
            this.reconnect()
        }
    }

    public updateQueryParams(params: Record<string, QueryValue>): void {
        this.queryParams = params
        if (this.socket) {
            this.reconnect()
        }
    }

    public connect(): void {
        if (this.socket) {
            return
        }

        const query = this.buildQueryObject()
        const url = this.buildSocketUrl(query)
        const socketOptions: Partial<ManagerOptions & SocketOptions> = {
            path: this.normalizeEndpoint(this.config.endpoint),
            transports: ['websocket'],
            upgrade: false,
            rejectUnauthorized: false,
            reconnectionDelay: this.config.reconnectionDelay ?? DEFAULT_RECONNECTION_DELAY,
            reconnectionAttempts: Infinity,
            timeout: 20000,
            query
        }

        logger.log('SOCKET', '🔌 Creating socket connection', {
            url,
            options: socketOptions,
            endpoint: this.normalizeEndpoint(this.config.endpoint)
        })
        
        const factory = this.socketFactory ?? ((factoryUrl: string, options?: Partial<ManagerOptions & SocketOptions>) => io(factoryUrl, options))
        this.socket = factory(url, socketOptions)
        
        logger.log('SOCKET', '📝 Socket instance created', {
            socketId: this.socket.id,
            connected: this.socket.connected,
            disconnected: this.socket.disconnected
        })

        // Check if already connected before registering handlers
        if (this.socket.connected) {
            logger.log('SOCKET', '⚠️ Socket already connected when registering handlers!')
            this.setConnectionState(true)
        }
        
        this.registerCoreHandlers()
        this.registerEventListeners()
        
        logger.log('SOCKET', '✅ Handlers registered', {
            socketId: this.socket.id,
            connected: this.socket.connected
        })
    }

    public disconnect(): void {
        if (!this.socket) {
            return
        }
        this.socket.off()
        this.socket.close()
        this.socket = undefined
        this.setConnectionState(false)
    }

    public reconnect(): void {
        this.disconnect()
        this.connect()
    }

    public checkConnection(): void {
        if (!this.socket && !this.isConnected) {
            this.connect()
        }
    }

    public on$<TEvent extends keyof TEvents>(event: TEvent): Observable<TEvents[TEvent]> {
        const eventName = String(event)
        const isNewEvent = !this.subjects.has(event)
        
        if (isNewEvent) {
            this.subjects.set(event, new Subject<TEvents[TEvent]>() as Subject<unknown>)
            logger.log('SOCKET', `📝 Created new subject for event "${eventName}"`)
        }

        const subject = this.subjects.get(event) as Subject<TEvents[TEvent]> | undefined

        if (!subject) {
            throw new Error(`Failed to initialize subject for socket event "${eventName}"`)
        }

        // Register socket listener for this event if socket is connected
        // Only register for new events to avoid duplicate listeners
        if (this.socket && isNewEvent) {
            logger.log('SOCKET', `📡 Registering socket listener for event "${eventName}"`)
            
            this.socket.on(eventName, (payload: unknown) => {
                logger.log('SOCKET', `📥 Direct event received: "${eventName}"`, {
                    event: eventName,
                    payload,
                    payloadType: typeof payload,
                    payloadPreview: typeof payload === 'object' 
                        ? JSON.stringify(payload).substring(0, 500)
                        : String(payload).substring(0, 500)
                })
                
                const currentSubject = this.subjects.get(event) as Subject<unknown> | undefined
                if (currentSubject) {
                    logger.log('SOCKET', `✅ Routing direct event "${eventName}" to subject`)
                    currentSubject.next(payload)
                } else {
                    logger.warn('SOCKET', `⚠️ Subject not found for direct event "${eventName}"`)
                }
            })
        }

        return subject.asObservable()
    }

    public off<TEvent extends keyof TEvents>(event: TEvent): this {
        const subject = this.subjects.get(event) as Subject<TEvents[TEvent]> | undefined
        if (subject) {
            subject.complete()
            subject.unsubscribe()
            this.subjects.delete(event)
        }

        return this
    }

    public offOn$<TEvent extends keyof TEvents>(event: TEvent): Observable<TEvents[TEvent]> {
        return this.off(event).on$(event)
    }

    public has<TEvent extends keyof TEvents>(event: TEvent): boolean {
        return this.subjects.has(event)
    }

    public emit<TEvent extends keyof TEvents>(event: TEvent, payload: TEvents[TEvent]): void {
        this.socket?.emit(event as string, payload)
    }

    public destroy(): void {
        this.disconnect()
        Array.from(this.subjects.keys()).forEach(event => this.off(event))
        this.isConnected$.complete()
    }

    public close(): void {
        this.disconnect()
    }

    protected onDisconnect(reason?: string): void {
        void reason
    }

    protected handleMessage(message: SharedSocketMessage<TEvents> | null, channel: string): void {
        logger.log('SOCKET', `🔍 handleMessage called`, {
            channel,
            message,
            hasType: message?.type !== undefined,
            messageType: message?.type,
            availableSubjects: Array.from(this.subjects.keys())
        })

        if (!message || !message.type) {
            logger.warn('SOCKET', '❌ Received malformed socket message', {
                channel,
                message,
                messageType: typeof message
            })
            return
        }

        if (!this.isDeviceAllowed(message.device)) {
            logger.log('SOCKET', `🚫 Message filtered (device not allowed): ${String(message.type)}`, {
                device: message.device,
                allowedDevices: this.allowedDevices,
                messageType: message.type
            })
            return
        }

        const subject = this.subjects.get(message.type as keyof TEvents) as
            | Subject<TEvents[keyof TEvents]>
            | undefined

        if (!subject) {
            logger.log('SOCKET', `⚠️ No observers for socket event "${String(message.type)}"`, {
                event: message.type,
                channel,
                availableSubjects: Array.from(this.subjects.keys()),
                message
            })
            return
        }

        logger.log('SOCKET', `✅ Routing event "${String(message.type)}" to subject`, {
            eventType: String(message.type),
            channel,
            device: message.device,
            target: message.target,
            payload: message.payload,
            payloadType: typeof message.payload,
            payloadPreview: typeof message.payload === 'object' 
                ? JSON.stringify(message.payload).substring(0, 500)
                : String(message.payload).substring(0, 500)
        })

        subject.next(message.payload)
        logger.log('SOCKET', '✅ Payload sent to subject for event:', String(message.type))
    }

    private registerEventListeners(): void {
        if (!this.socket) {
            return
        }

        // Register socket listeners for all existing subjects
        this.subjects.forEach((_subject, event) => {
            const eventName = String(event)
            logger.log('SOCKET', `📝 Registering socket listener for existing event "${eventName}"`)
            
            this.socket!.on(eventName, (payload: unknown) => {
                logger.log('SOCKET', `📥 Direct event received: "${eventName}"`, {
                    event: eventName,
                    payload,
                    payloadType: typeof payload,
                    payloadPreview: typeof payload === 'object' 
                        ? JSON.stringify(payload).substring(0, 500)
                        : String(payload).substring(0, 500)
                })
                
                const currentSubject = this.subjects.get(event) as Subject<unknown> | undefined
                if (currentSubject) {
                    logger.log('SOCKET', `✅ Routing direct event "${eventName}" to subject`)
                    currentSubject.next(payload)
                } else {
                    logger.warn('SOCKET', `⚠️ Subject not found for direct event "${eventName}"`)
                }
            })
        })
    }

    private registerCoreHandlers(): void {
        if (!this.socket) {
            logger.error('SOCKET', '❌ Cannot register handlers - socket is null')
            return
        }

        logger.log('SOCKET', '📝 Registering core handlers...')

        // Add a catch-all listener using the manager to see ALL events
        // Also manually handle packets if events aren't being emitted automatically
        const manager = this.socket.io
        if (manager) {
            manager.on('packet', (packet: { type: number; nsp?: string; data?: unknown }) => {
                logger.log('SOCKET', '📦 Raw Socket.IO packet received', {
                    type: packet.type,
                    nsp: packet.nsp,
                    data: packet.data,
                    dataPreview: Array.isArray(packet.data) 
                        ? packet.data.map((d: unknown) => typeof d === 'string' ? d.substring(0, 200) : typeof d)
                        : typeof packet.data
                })
                
                // Type 2 is EVENT packet in Socket.IO protocol
                // If data is an array like ['u', '{...json...}'], manually process it
                if (packet.type === 2 && Array.isArray(packet.data) && packet.data.length >= 2) {
                    const eventName = packet.data[0]
                    const eventPayload = packet.data[1]
                    
                    logger.log('SOCKET', '🔍 Processing EVENT packet manually', {
                        eventName,
                        eventPayloadType: typeof eventPayload,
                        eventPayloadPreview: typeof eventPayload === 'string' 
                            ? eventPayload.substring(0, 200) 
                            : JSON.stringify(eventPayload).substring(0, 200)
                    })
                    
                    // Handle 'u' and 'b' channel events manually
                    if (eventName === 'u' || eventName === 'b') {
                        logger.log('SOCKET', `📨 Manually processing "${eventName}" channel event from packet`)
                        const parsed = this.parseIncomingPayload(eventPayload)
                        this.handleMessage(parsed, eventName)
                    }
                }
            })
        }

        this.socket.on('connect', () => {
            logger.log('SOCKET', '✅ Socket connected', {
                id: this.socket?.id,
                transport: this.socket?.io?.engine?.transport?.name
            })
            this.setConnectionState(true)
        })

        this.socket.on('disconnect', reason => {
            logger.log('SOCKET', '❌ Socket disconnected', reason)
            this.setConnectionState(false)
            this.onDisconnect(reason)
        })

        this.socket.on('error', error => {
            logger.error('SOCKET', 'Socket error', error)
            this.notifySubjectsOfError(error)
        })

        // Listen to channel-based events (legacy)
        logger.log('SOCKET', '📝 Registering "u" channel handler...')
        this.socket.on('u', (...args) => {
            logger.log('SOCKET', '📨 ========== "u" EVENT RECEIVED ==========', {
                timestamp: new Date().toISOString(),
                argsCount: args.length,
                allArgs: args,
                firstArg: args[0],
                firstArgType: typeof args[0],
                firstArgPreview: typeof args[0] === 'string' 
                    ? args[0].substring(0, 500) 
                    : JSON.stringify(args[0]).substring(0, 500)
            })
            
            // Socket.IO might pass multiple arguments, get the first one
            const payload = args[0]
            
            logger.log('SOCKET', '📨 Raw message on channel "u"', {
                payload,
                payloadType: typeof payload,
                isString: typeof payload === 'string',
                isObject: typeof payload === 'object',
                payloadLength: typeof payload === 'string' ? payload.length : 'N/A'
            })
            
            const parsed = this.parseIncomingPayload(payload)
            logger.log('SOCKET', '🔍 Parsed message from "u" channel', {
                parsed,
                hasType: parsed?.type !== undefined,
                type: parsed?.type,
                hasPayload: parsed?.payload !== undefined,
                payloadPreview: parsed?.payload ? JSON.stringify(parsed.payload).substring(0, 200) : 'N/A'
            })
            this.handleMessage(parsed, 'u')
        })
        logger.log('SOCKET', '✅ "u" channel handler registered')
        this.socket.on('b', payload => {
            logger.log('SOCKET', '📨 Raw message on channel "b"', payload)
            this.handleMessage(this.parseIncomingPayload(payload), 'b')
        })

        if (this.config.msgpack) {
            this.socket.on('ump', payload => {
                logger.log('SOCKET', '📨 Raw message on channel "ump"', payload)
                this.handleMessage(this.parseIncomingPayload(payload), 'ump')
            })
            this.socket.on('bmp', payload => {
                logger.log('SOCKET', '📨 Raw message on channel "bmp"', payload)
                this.handleMessage(this.parseIncomingPayload(payload), 'bmp')
            })
        }


    }

    private notifySubjectsOfError(error: unknown): void {
        this.subjects.forEach(subject => subject.error(error))
    }

    private parseIncomingPayload(payload: unknown): SharedSocketMessage<TEvents> | null {
        logger.log('SOCKET', '🔍 parseIncomingPayload called', {
            payload,
            payloadType: typeof payload,
            isArrayBuffer: payload instanceof ArrayBuffer,
            isArrayBufferView: ArrayBuffer.isView(payload),
            isString: typeof payload === 'string',
            isObject: typeof payload === 'object' && payload !== null,
            stringPreview: typeof payload === 'string' ? payload.substring(0, 200) : 'N/A'
        })

        try {
            if (typeof payload === 'string') {
                logger.log('SOCKET', '🔍 Attempting to parse JSON string...', payload.substring(0, 500))
                const parsed = JSON.parse(payload) as SharedSocketMessage<TEvents>
                logger.log('SOCKET', '✅ Parsed string payload', parsed)
                return parsed
            }

            if (payload instanceof ArrayBuffer) {
                const decoded = decode(new Uint8Array(payload)) as SharedSocketMessage<TEvents>
                logger.log('SOCKET', '✅ Decoded ArrayBuffer payload', decoded)
                return decoded
            }

            if (ArrayBuffer.isView(payload)) {
                const view = payload as ArrayBufferView
                const decoded = decode(
                    new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
                ) as SharedSocketMessage<TEvents>
                logger.log('SOCKET', '✅ Decoded ArrayBufferView payload', decoded)
                return decoded
            }

            if (typeof payload === 'object' && payload !== null) {
                // Check if it's already a SharedSocketMessage
                if ('type' in payload && 'payload' in payload) {
                    logger.log('SOCKET', '✅ Payload is already SharedSocketMessage format', payload)
                    return payload as SharedSocketMessage<TEvents>
                }
                
                // If it's a plain object without type/payload structure, wrap it
                // This handles cases where the payload is the message itself
                logger.log('SOCKET', '⚠️ Payload is object but not SharedSocketMessage format, attempting to use as-is', payload)
                return payload as SharedSocketMessage<TEvents>
            }
        } catch (error) {
            logger.warn('SOCKET', '❌ Unable to parse incoming socket payload', {
                error,
                payload,
                payloadType: typeof payload
            })
            return null
        }

        logger.warn('SOCKET', '❌ Unsupported socket payload format', {
            payload,
            payloadType: typeof payload
        })
        return null
    }

    private isDeviceAllowed(device?: string): boolean {
        if (!device) {
            return true;
        }
        return this.allowedDevices.includes(device ?? 'all')
    }

    private buildSocketUrl(query?: Record<string, string>): string {
        const baseUrl = `${this.config.protocol}://${this.config.host}:${this.config.port}`
        const queryString = this.buildQueryString(query)

        if (!queryString) {
            return baseUrl
        }

        return `${baseUrl}?${queryString}`
    }

    private buildQueryString(query?: Record<string, string>): string {
        const queryObject = query ?? this.buildQueryObject()
        const entries = Object.entries(queryObject)

        if (!entries.length) {
            return ''
        }

        const searchParams = new URLSearchParams()
        entries.forEach(([key, value]) => {
            searchParams.append(key, value)
        })

        return searchParams.toString()
    }

    private buildQueryObject(): Record<string, string> {
        const params = {
            ...this.queryParams,
            ...(this.buildQueryParams?.() ?? {})
        }

        if (this.config.msgpack) {
            params.format = 'msgpack'
        }

        return Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
            if (value !== undefined && value !== null) {
                acc[key] = String(value)
            }
            return acc
        }, {})
    }

    private normalizeEndpoint(endpoint: string): string {
        if (!endpoint.startsWith('/')) {
            return `/${endpoint}`
        }
        return endpoint
    }

    private setConnectionState(isConnected: boolean): void {
        this.isConnected = isConnected
        this.isConnected$.next(isConnected)
    }
}

